import { useEffect, useMemo, useState } from 'react';
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Card,
  Group,
  LoadingOverlay,
  Modal,
  NumberInput,
  Radio,
  Stack,
  Table,
  Text,
  Textarea,
  TextInput,
  Title,
  Tooltip,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { useDisclosure } from '@mantine/hooks';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import {
  IconBan,
  IconCash,
  IconCheck,
  IconEdit,
  IconLock,
  IconPlus,
  IconRefresh,
  IconSend,
  IconTrash,
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import type {
  PaymentRequirement,
  PaymentRequirementStatus,
  PaymentRequirementTarget,
  Student,
} from '@pta/types';
import {
  cancelRequirement,
  closeRequirement,
  deleteRequirement,
  fetchRequirement,
  publishRequirement,
  removeRequirementTarget,
  updateRequirement,
} from '../api/payment-requirements';
import {
  fetchStudentsByGroup,
} from '../api/core-entities';
import { fetchGroupPayments } from '../api/payments';
import { ApiError } from '../api/client';
import { formatTimestamp, toIsoDate } from '../util/format';
import { useMoneyFormatter } from '../util/money';
import { RequirementTargetFormModal } from '../components/RequirementTargetFormModal';
import { PaymentFormModal } from '../components/PaymentFormModal';
import { useAuth } from '../auth/AuthProvider';

/**
 * F2b — Befizetési előírás részlet-oldal.
 *
 * Pénzformázás: a csoport `GroupLocale`-jából (useMoneyFormatter).
 */
export function RequirementDetailPage() {
  const { reqId: reqIdStr } = useParams<{ reqId: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { myGroups, activeRole } = useAuth();
  const { money } = useMoneyFormatter();
  const requirementId = Number(reqIdStr);

  const canRecordPayment =
    activeRole === 'pta_cashier' ||
    activeRole === 'institution_admin' ||
    activeRole === 'country_admin' ||
    activeRole === 'systemadmin' ||
    activeRole === 'applicationadmin';

  const [requirement, setRequirement] = useState<PaymentRequirement | null>(null);
  const [targets, setTargets] = useState<PaymentRequirementTarget[]>([]);
  const [students, setStudents] = useState<Student[]>([]);
  const [paidByTarget, setPaidByTarget] = useState<Map<number, string>>(new Map());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [editOpened, { open: openEdit, close: closeEdit }] = useDisclosure();
  const [targetModalOpened, { open: openTargetModal, close: closeTargetModal }] =
    useDisclosure();
  const [editingTarget, setEditingTarget] = useState<PaymentRequirementTarget | null>(
    null,
  );
  const [cancelOpened, { open: openCancel, close: closeCancel }] = useDisclosure();

  const [paymentModalOpened, { open: openPaymentModal, close: closePaymentModal }] =
    useDisclosure();
  const [paymentTarget, setPaymentTarget] = useState<PaymentRequirementTarget | null>(
    null,
  );

  const group = useMemo(() => {
    if (!requirement) return null;
    return myGroups.find((g) => g.id === requirement.groupId) ?? null;
  }, [myGroups, requirement]);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const withTargets = await fetchRequirement(requirementId, { includeTargets: true });
      setRequirement(withTargets);
      const ts = withTargets.targets ?? [];
      setTargets(ts);

      if (
        withTargets.status === 'published' ||
        withTargets.status === 'closed'
      ) {
        try {
          const payments = await fetchGroupPayments(withTargets.groupId, {
            requirementId: withTargets.id,
          });
          const totals = new Map<number, string>();
          for (const tg of ts) totals.set(tg.id, '0');
          for (const p of payments) {
            const cur = Number(totals.get(p.targetId) ?? '0');
            totals.set(p.targetId, (cur + Number(p.amount)).toFixed(2));
          }
          setPaidByTarget(totals);
        } catch {
          setPaidByTarget(new Map());
        }
      } else {
        setPaidByTarget(new Map());
      }
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!requirement) return;
    const groupId = requirement.groupId;
    void (async () => {
      try {
        const studs = await fetchStudentsByGroup(groupId);
        setStudents(studs);
      } catch (e) {
        setError(e instanceof ApiError ? e.message : String(e));
      }
    })();
  }, [requirement]);

  useEffect(() => {
    if (Number.isFinite(requirementId)) {
      void load();
    }
  }, [requirementId]);

  const studentById = useMemo(() => {
    const map = new Map<number, Student>();
    for (const s of students) map.set(s.id, s);
    return map;
  }, [students]);

  if (!Number.isFinite(requirementId)) {
    return (
      <Alert color="red">
        {t('common.invalid_id', { ns: 'common', defaultValue: 'Érvénytelen azonosító' })}
      </Alert>
    );
  }

  const handlePublish = () => {
    if (!requirement) return;
    modals.openConfirmModal({
      title: t('requirements.publish_confirm_title', {
        ns: 'fe',
        defaultValue: 'Előírás publikálása',
      }),
      children: (
        <Stack gap="xs">
          <Text>
            {t('requirements.publish_confirm_body', {
              ns: 'fe',
              defaultValue:
                'A publikálás után a szülők a FE-n látni fogják ezt az előírást. A cím, cél és deviza ezután már nem módosítható. Publikálod?',
            })}
          </Text>
          <Text fw={500}>
            {requirement.title} — {money(requirement.amount)}
          </Text>
        </Stack>
      ),
      labels: {
        confirm: t('requirements.publish_btn', { ns: 'fe', defaultValue: 'Publikálás' }),
        cancel: t('common.cancel', { ns: 'common', defaultValue: 'Mégse' }),
      },
      confirmProps: { color: 'green' },
      onConfirm: async () => {
        try {
          await publishRequirement(requirement.id);
          notifications.show({
            color: 'green',
            message: t('requirements.published_msg', {
              ns: 'fe',
              defaultValue: 'Előírás publikálva',
            }),
          });
          await load();
        } catch (e) {
          notifications.show({
            color: 'red',
            message: e instanceof ApiError ? e.message : String(e),
          });
        }
      },
    });
  };

  const handleDelete = () => {
    if (!requirement) return;
    modals.openConfirmModal({
      title: t('requirements.delete_confirm_title', {
        ns: 'fe',
        defaultValue: 'Draft törlése',
      }),
      children: (
        <Stack gap="xs">
          <Text>
            {t('requirements.delete_confirm_body', {
              ns: 'fe',
              defaultValue:
                'A draft előírás és az összes target véglegesen törlődik. Ez a művelet nem visszavonható.',
            })}
          </Text>
          <Text fw={500}>{requirement.title}</Text>
        </Stack>
      ),
      labels: {
        confirm: t('common.delete', { ns: 'common', defaultValue: 'Törlés' }),
        cancel: t('common.cancel', { ns: 'common', defaultValue: 'Mégse' }),
      },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        try {
          await deleteRequirement(requirement.id);
          notifications.show({
            color: 'green',
            message: t('requirements.deleted_msg', {
              ns: 'fe',
              defaultValue: 'Draft törölve',
            }),
          });
          navigate(`/app/requirements`);
        } catch (e) {
          notifications.show({
            color: 'red',
            message: e instanceof ApiError ? e.message : String(e),
          });
        }
      },
    });
  };

  const handleClose = () => {
    if (!requirement) return;
    modals.openConfirmModal({
      title: t('requirements.close_confirm_title', {
        ns: 'fe',
        defaultValue: 'Előírás lezárása',
      }),
      children: (
        <Text>
          {t('requirements.close_confirm_body', {
            ns: 'fe',
            defaultValue:
              'A lezárt előíráson már nem lehet target-et módosítani vagy új befizetést rögzíteni. Folytatod?',
          })}
        </Text>
      ),
      labels: {
        confirm: t('requirements.close_btn', { ns: 'fe', defaultValue: 'Lezárás' }),
        cancel: t('common.cancel', { ns: 'common', defaultValue: 'Mégse' }),
      },
      confirmProps: { color: 'blue' },
      onConfirm: async () => {
        try {
          await closeRequirement(requirement.id);
          notifications.show({
            color: 'green',
            message: t('requirements.closed_msg', {
              ns: 'fe',
              defaultValue: 'Előírás lezárva',
            }),
          });
          await load();
        } catch (e) {
          notifications.show({
            color: 'red',
            message: e instanceof ApiError ? e.message : String(e),
          });
        }
      },
    });
  };

  const handleRemoveTarget = (target: PaymentRequirementTarget) => {
    const student = studentById.get(target.studentId);
    const studentName = student
      ? `${student.lastName} ${student.firstName}`
      : `#${target.studentId}`;
    modals.openConfirmModal({
      title: t('requirements.target_remove_title', {
        ns: 'fe',
        defaultValue: 'Target törlése',
      }),
      children: (
        <Stack gap="xs">
          <Text>
            {t('requirements.target_remove_body', {
              ns: 'fe',
              defaultValue: 'Biztosan törlöd ezt a target-et?',
            })}
          </Text>
          <Text fw={500}>{studentName}</Text>
        </Stack>
      ),
      labels: {
        confirm: t('common.delete', { ns: 'common', defaultValue: 'Törlés' }),
        cancel: t('common.cancel', { ns: 'common', defaultValue: 'Mégse' }),
      },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        try {
          await removeRequirementTarget(target.id);
          notifications.show({
            color: 'green',
            message: t('requirements.target_removed', {
              ns: 'fe',
              defaultValue: 'Target törölve',
            }),
          });
          await load();
        } catch (e) {
          notifications.show({
            color: 'red',
            message: e instanceof ApiError ? e.message : String(e),
          });
        }
      },
    });
  };

  const handleOpenTargetEdit = (target: PaymentRequirementTarget) => {
    setEditingTarget(target);
    openTargetModal();
  };

  const handleOpenTargetNew = () => {
    setEditingTarget(null);
    openTargetModal();
  };

  const handleOpenPayment = (target: PaymentRequirementTarget) => {
    setPaymentTarget(target);
    openPaymentModal();
  };

  const statusBadgeColor = (s: PaymentRequirementStatus): string => {
    switch (s) {
      case 'draft':
        return 'gray';
      case 'published':
        return 'green';
      case 'cancelled':
        return 'red';
      case 'closed':
        return 'blue';
    }
  };

  if (!requirement) {
    return (
      <Stack gap="md" pos="relative">
        <LoadingOverlay visible={loading} />
        {error && <Alert color="red">{error}</Alert>}
      </Stack>
    );
  }

  const isReadOnly =
    requirement.status === 'cancelled' || requirement.status === 'closed';
  const canModifyTargets = !isReadOnly;
  const paymentsAllowed =
    requirement.status === 'published' || requirement.status === 'closed';

  const groupContextLabel = (() => {
    if (!group) return `#${requirement.groupId}`;
    if (group.institutionLabel) return `${group.institutionLabel} — ${group.name}`;
    return group.name;
  })();

  return (
    <Stack gap="md" pos="relative">
      <LoadingOverlay visible={loading} />

      <Group gap="xs">
        <Link to="/app/requirements">
          <Text size="sm" c="dimmed">
            ← {t('requirements.back_to_list', {
              ns: 'fe',
              defaultValue: 'Vissza a listához',
            })}
          </Text>
        </Link>
        <Text size="sm" c="dimmed">
          • {groupContextLabel}
        </Text>
      </Group>

      <Group justify="space-between">
        <Group gap="sm">
          <Title order={2}>
            #{requirement.id} — {requirement.title}
          </Title>
          <Badge color={statusBadgeColor(requirement.status)} size="lg">
            {t(`requirements.status_${requirement.status}`, {
              ns: 'fe',
              defaultValue: requirement.status,
            })}
          </Badge>
        </Group>
        <Group>
          <Tooltip label={t('common.refresh', { ns: 'common', defaultValue: 'Frissítés' })}>
            <ActionIcon onClick={() => void load()} variant="light">
              <IconRefresh size={18} />
            </ActionIcon>
          </Tooltip>

          {!isReadOnly && (
            <Button
              leftSection={<IconEdit size={16} />}
              variant="light"
              onClick={openEdit}
            >
              {t('common.edit', { ns: 'common', defaultValue: 'Szerkesztés' })}
            </Button>
          )}

          {requirement.status === 'draft' && (
            <>
              <Button
                leftSection={<IconSend size={16} />}
                color="green"
                onClick={handlePublish}
              >
                {t('requirements.publish_btn', { ns: 'fe', defaultValue: 'Publikálás' })}
              </Button>
              <Button
                leftSection={<IconTrash size={16} />}
                color="red"
                variant="light"
                onClick={handleDelete}
              >
                {t('common.delete', { ns: 'common', defaultValue: 'Törlés' })}
              </Button>
            </>
          )}

          {requirement.status === 'published' && (
            <>
              <Button
                leftSection={<IconLock size={16} />}
                color="blue"
                variant="light"
                onClick={handleClose}
              >
                {t('requirements.close_btn', { ns: 'fe', defaultValue: 'Lezárás' })}
              </Button>
              <Button
                leftSection={<IconBan size={16} />}
                color="red"
                variant="light"
                onClick={openCancel}
              >
                {t('requirements.cancel_btn', {
                  ns: 'fe',
                  defaultValue: 'Visszavonás',
                })}
              </Button>
            </>
          )}
        </Group>
      </Group>

      {error && <Alert color="red">{error}</Alert>}

      <Card withBorder>
        <Stack gap="xs">
          <Group gap="xl">
            <KV
              label={t('requirements.col_purpose', { ns: 'fe', defaultValue: 'Cél' })}
              value={requirement.purpose}
            />
            <KV
              label={t('requirements.col_amount', { ns: 'fe', defaultValue: 'Összeg' })}
              value={money(requirement.amount)}
            />
            <KV
              label={t('requirements.col_due_date', { ns: 'fe', defaultValue: 'Határidő' })}
              value={requirement.dueDate ?? '—'}
            />
          </Group>

          {requirement.description && (
            <div>
              <Text size="sm" fw={500}>
                {t('requirements.col_description', { ns: 'fe', defaultValue: 'Leírás' })}
              </Text>
              <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
                {requirement.description}
              </Text>
            </div>
          )}

          <Group gap="xl" mt="xs">
            <KV
              label={t('requirements.created_at', {
                ns: 'fe',
                defaultValue: 'Létrehozva',
              })}
              value={`${formatTimestamp(requirement.createdAt)} (user #${requirement.createdBy})`}
            />
            {requirement.publishedAt && (
              <KV
                label={t('requirements.published_at', {
                  ns: 'fe',
                  defaultValue: 'Publikálva',
                })}
                value={formatTimestamp(requirement.publishedAt)}
              />
            )}
            {requirement.closedAt && (
              <KV
                label={t('requirements.closed_at', { ns: 'fe', defaultValue: 'Lezárva' })}
                value={formatTimestamp(requirement.closedAt)}
              />
            )}
          </Group>

          {requirement.status === 'cancelled' && requirement.cancelledAt && (
            <Alert
              color="red"
              variant="light"
              title={t('requirements.cancelled_alert', {
                ns: 'fe',
                defaultValue: 'Visszavont előírás',
              })}
            >
              <Text size="sm">{formatTimestamp(requirement.cancelledAt)}</Text>
              {requirement.cancelReason && (
                <Text size="sm" mt="xs">
                  <strong>
                    {t('requirements.cancel_reason', {
                      ns: 'fe',
                      defaultValue: 'Indok',
                    })}
                    :
                  </strong>{' '}
                  {requirement.cancelReason}
                </Text>
              )}
            </Alert>
          )}
        </Stack>
      </Card>

      <Card withBorder p={0}>
        <Group justify="space-between" p="md">
          <Title order={4}>
            {t('requirements.targets_title', {
              ns: 'fe',
              defaultValue: 'Célzott diákok',
            })}{' '}
            <Text span c="dimmed" size="sm">
              ({targets.length})
            </Text>
          </Title>
          {canModifyTargets && (
            <Button
              size="xs"
              leftSection={<IconPlus size={14} />}
              onClick={handleOpenTargetNew}
            >
              {t('requirements.target_add_btn', {
                ns: 'fe',
                defaultValue: 'Új target',
              })}
            </Button>
          )}
        </Group>
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>
                {t('requirements.target_student', { ns: 'fe', defaultValue: 'Diák' })}
              </Table.Th>
              <Table.Th ta="right">
                {t('requirements.target_amount', {
                  ns: 'fe',
                  defaultValue: 'Várt összeg',
                })}
              </Table.Th>
              {paymentsAllowed && (
                <>
                  <Table.Th ta="right">
                    {t('requirements.target_paid', {
                      ns: 'fe',
                      defaultValue: 'Eddig fizetve',
                    })}
                  </Table.Th>
                  <Table.Th ta="right">
                    {t('requirements.target_remaining', {
                      ns: 'fe',
                      defaultValue: 'Hátralék',
                    })}
                  </Table.Th>
                </>
              )}
              <Table.Th>
                {t('requirements.target_waived_col', {
                  ns: 'fe',
                  defaultValue: 'Mentesítve',
                })}
              </Table.Th>
              <Table.Th style={{ width: 140 }}>
                {t('common.actions', {
                  ns: 'common',
                  defaultValue: 'Műveletek',
                })}
              </Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {targets.length === 0 && (
              <Table.Tr>
                <Table.Td colSpan={paymentsAllowed ? 6 : 4} align="center">
                  {t('common.no_results', { ns: 'common', defaultValue: 'Nincs találat' })}
                </Table.Td>
              </Table.Tr>
            )}
            {targets.map((tg) => {
              const student = studentById.get(tg.studentId);
              const studentName = student
                ? `${student.lastName} ${student.firstName}`
                : `#${tg.studentId}`;
              const paid = paidByTarget.get(tg.id) ?? '0';
              const expected = Number(tg.expectedAmount);
              const remaining = Math.max(0, expected - Number(paid));
              const fullyPaid = paymentsAllowed && !tg.waived && remaining === 0;
              return (
                <Table.Tr key={tg.id}>
                  <Table.Td>{studentName}</Table.Td>
                  <Table.Td ta="right">{money(tg.expectedAmount)}</Table.Td>
                  {paymentsAllowed && (
                    <>
                      <Table.Td ta="right">
                        {tg.waived ? (
                          <Text size="sm" c="dimmed">—</Text>
                        ) : (
                          <Text size="sm">{money(paid)}</Text>
                        )}
                      </Table.Td>
                      <Table.Td ta="right">
                        {tg.waived ? (
                          <Text size="sm" c="dimmed">—</Text>
                        ) : fullyPaid ? (
                          <Badge color="teal" variant="light" size="sm">
                            {t('requirements.target_fully_paid', {
                              ns: 'fe',
                              defaultValue: 'Teljesítve',
                            })}
                          </Badge>
                        ) : (
                          <Text size="sm" fw={500}>{money(remaining)}</Text>
                        )}
                      </Table.Td>
                    </>
                  )}
                  <Table.Td>
                    {tg.waived ? (
                      <Stack gap={0}>
                        <Badge color="orange" variant="light" size="sm">
                          {t('requirements.target_waived_yes', {
                            ns: 'fe',
                            defaultValue: 'Igen',
                          })}
                        </Badge>
                        {tg.waiveReason && (
                          <small style={{ color: '#888' }}>{tg.waiveReason}</small>
                        )}
                      </Stack>
                    ) : (
                      <Text size="sm" c="dimmed">—</Text>
                    )}
                  </Table.Td>
                  <Table.Td>
                    <Group gap={4}>
                      {paymentsAllowed && canRecordPayment && !tg.waived && (
                        <Tooltip
                          label={t('requirements.target_pay_btn', {
                            ns: 'fe',
                            defaultValue: 'Befizetés rögzítése',
                          })}
                        >
                          <ActionIcon
                            variant="subtle"
                            color="teal"
                            onClick={() => handleOpenPayment(tg)}
                          >
                            <IconCash size={16} />
                          </ActionIcon>
                        </Tooltip>
                      )}
                      <ActionIcon
                        variant="subtle"
                        onClick={() => handleOpenTargetEdit(tg)}
                        disabled={!canModifyTargets}
                      >
                        <IconEdit size={16} />
                      </ActionIcon>
                      <ActionIcon
                        variant="subtle"
                        color="red"
                        onClick={() => handleRemoveTarget(tg)}
                        disabled={!canModifyTargets}
                      >
                        <IconTrash size={16} />
                      </ActionIcon>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              );
            })}
          </Table.Tbody>
        </Table>
      </Card>

      <RequirementEditModal
        opened={editOpened}
        onClose={closeEdit}
        requirement={requirement}
        onSaved={async () => {
          closeEdit();
          await load();
        }}
      />

      <RequirementTargetFormModal
        opened={targetModalOpened}
        onClose={closeTargetModal}
        requirement={requirement}
        target={editingTarget}
        groupStudents={students}
        existingTargets={targets}
        onSaved={async () => {
          closeTargetModal();
          await load();
        }}
      />

      <CancelRequirementModal
        opened={cancelOpened}
        onClose={closeCancel}
        requirement={requirement}
        onSaved={async () => {
          closeCancel();
          await load();
        }}
      />

      {paymentTarget && (
        <PaymentFormModal
          opened={paymentModalOpened}
          onClose={() => {
            closePaymentModal();
            setPaymentTarget(null);
          }}
          groupId={requirement.groupId}
          target={paymentTarget}
          studentName={(() => {
            const s = studentById.get(paymentTarget.studentId);
            return s ? `${s.lastName} ${s.firstName}` : `#${paymentTarget.studentId}`;
          })()}
          currency={requirement.currency}
          paidSoFar={paidByTarget.get(paymentTarget.id) ?? '0'}
          onSaved={async () => {
            closePaymentModal();
            setPaymentTarget(null);
            await load();
          }}
        />
      )}
    </Stack>
  );
}

function KV({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <Text size="xs" c="dimmed">{label}</Text>
      <Text size="sm" fw={500}>{value}</Text>
    </div>
  );
}

interface RequirementEditModalProps {
  opened: boolean;
  onClose: () => void;
  requirement: PaymentRequirement;
  onSaved: () => void;
}

function RequirementEditModal({
  opened,
  onClose,
  requirement,
  onSaved,
}: RequirementEditModalProps) {
  const { t } = useTranslation();

  const isDraft = requirement.status === 'draft';

  const [title, setTitle] = useState(requirement.title);
  const [description, setDescription] = useState(requirement.description ?? '');
  const [purpose, setPurpose] = useState(requirement.purpose);
  const [amount, setAmount] = useState<number | ''>(Number(requirement.amount));
  const [dueDate, setDueDate] = useState<Date | null>(
    requirement.dueDate ? new Date(requirement.dueDate) : null,
  );
  const [updateMode, setUpdateMode] = useState<'amount_only' | 'full'>('amount_only');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!opened) return;
    setTitle(requirement.title);
    setDescription(requirement.description ?? '');
    setPurpose(requirement.purpose);
    setAmount(Number(requirement.amount));
    setDueDate(requirement.dueDate ? new Date(requirement.dueDate) : null);
    setUpdateMode('amount_only');
  }, [opened, requirement]);

  const canSave =
    title.trim() !== '' &&
    purpose.trim() !== '' &&
    typeof amount === 'number' &&
    amount > 0;

  const fullEditable = isDraft || (!isDraft && updateMode === 'full');

  const handleSave = async () => {
    if (!canSave || typeof amount !== 'number') return;
    setSaving(true);
    try {
      const amountStr = amount.toFixed(2).replace(/\.00$/, '');
      const body: Parameters<typeof updateRequirement>[1] = {
        amount: amountStr,
        description: description.trim() || null,
        dueDate: dueDate ? toIsoDate(dueDate) : null,
      };
      if (fullEditable) {
        body.title = title.trim();
        body.purpose = purpose.trim();
      }
      if (!isDraft) {
        body.updateMode = updateMode;
      }
      await updateRequirement(requirement.id, body);
      notifications.show({
        color: 'green',
        message: t('requirements.updated_msg', {
          ns: 'fe',
          defaultValue: 'Előírás frissítve',
        }),
      });
      onSaved();
    } catch (e) {
      notifications.show({
        color: 'red',
        message: e instanceof ApiError ? e.message : String(e),
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      overlayProps={{ backgroundOpacity: 0.55, blur: 3 }}
      title={t('requirements.edit', {
        ns: 'fe',
        defaultValue: 'Előírás szerkesztése',
      })}
      size="lg"
    >
      <Stack>
        {!isDraft && (
          <Radio.Group
            label={t('requirements.update_mode', {
              ns: 'fe',
              defaultValue: 'Módosítási mód',
            })}
            description={t('requirements.update_mode_hint', {
              ns: 'fe',
              defaultValue:
                'Publikált előíráson csak az összeg / leírás / határidő módosítható alapértelmezésben. Full módban a cím és cél is — de a deviza soha nem.',
            })}
            value={updateMode}
            onChange={(v) => setUpdateMode((v as 'amount_only' | 'full') ?? 'amount_only')}
          >
            <Group mt="xs">
              <Radio
                value="amount_only"
                label={t('requirements.update_mode_amount_only', {
                  ns: 'fe',
                  defaultValue: 'Amount-only (ajánlott)',
                })}
              />
              <Radio
                value="full"
                label={t('requirements.update_mode_full', {
                  ns: 'fe',
                  defaultValue: 'Full (címet, célt is)',
                })}
              />
            </Group>
          </Radio.Group>
        )}

        <TextInput
          label={t('requirements.col_title', { ns: 'fe', defaultValue: 'Cím' })}
          value={title}
          onChange={(e) => setTitle(e.currentTarget.value)}
          disabled={!fullEditable}
          required
          maxLength={200}
        />

        <TextInput
          label={t('requirements.col_purpose', { ns: 'fe', defaultValue: 'Cél' })}
          value={purpose}
          onChange={(e) => setPurpose(e.currentTarget.value)}
          disabled={!fullEditable}
          required
          maxLength={200}
        />

        <Textarea
          label={t('requirements.col_description', {
            ns: 'fe',
            defaultValue: 'Leírás (opcionális)',
          })}
          value={description}
          onChange={(e) => setDescription(e.currentTarget.value)}
          autosize
          minRows={2}
          maxLength={5000}
        />

        <Group grow>
          <NumberInput
            label={t('requirements.col_amount', {
              ns: 'fe',
              defaultValue: 'Összeg',
            })}
            description={`${t('requirements.currency_label', {
              ns: 'fe',
              defaultValue: 'Deviza',
            })}: ${requirement.currency} ${t('requirements.currency_locked_hint', {
              ns: 'fe',
              defaultValue: '(publikálás után nem módosítható)',
            })}`}
            value={amount}
            onChange={(v) => setAmount(typeof v === 'number' ? v : '')}
            min={0}
            decimalScale={2}
            thousandSeparator=" "
            required
          />
        </Group>

        <DateInput
          label={t('requirements.col_due_date', {
            ns: 'fe',
            defaultValue: 'Határidő (opcionális)',
          })}
          value={dueDate}
          onChange={(v) => setDueDate(v ? new Date(v) : null)}
          valueFormat="YYYY-MM-DD"
          clearable
        />

        <Group justify="end">
          <Button variant="default" onClick={onClose} disabled={saving}>
            {t('common.cancel', { ns: 'common', defaultValue: 'Mégse' })}
          </Button>
          <Button
            leftSection={<IconCheck size={16} />}
            onClick={handleSave}
            loading={saving}
            disabled={!canSave}
          >
            {t('common.save', { ns: 'common', defaultValue: 'Mentés' })}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

interface CancelRequirementModalProps {
  opened: boolean;
  onClose: () => void;
  requirement: PaymentRequirement;
  onSaved: () => void;
}

function CancelRequirementModal({
  opened,
  onClose,
  requirement,
  onSaved,
}: CancelRequirementModalProps) {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (opened) setReason('');
  }, [opened]);

  const handleSave = async () => {
    if (reason.trim() === '') return;
    setSaving(true);
    try {
      await cancelRequirement(requirement.id, { cancelReason: reason.trim() });
      notifications.show({
        color: 'green',
        message: t('requirements.cancelled_msg', {
          ns: 'fe',
          defaultValue: 'Előírás visszavonva',
        }),
      });
      onSaved();
    } catch (e) {
      notifications.show({
        color: 'red',
        message: e instanceof ApiError ? e.message : String(e),
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      overlayProps={{ backgroundOpacity: 0.55, blur: 3 }}
      title={t('requirements.cancel_modal_title', {
        ns: 'fe',
        defaultValue: 'Előírás visszavonása',
      })}
      size="md"
    >
      <Stack>
        <Alert color="red" variant="light">
          {t('requirements.cancel_modal_warning', {
            ns: 'fe',
            defaultValue:
              'A visszavonás után az előírás már nem módosítható és a szülők sem látják publikáltan. Adj meg indokot (auditban rögzül).',
          })}
        </Alert>
        <Textarea
          label={t('requirements.cancel_reason', {
            ns: 'fe',
            defaultValue: 'Indok',
          })}
          value={reason}
          onChange={(e) => setReason(e.currentTarget.value)}
          autosize
          minRows={3}
          maxLength={500}
          required
        />
        <Group justify="end">
          <Button variant="default" onClick={onClose} disabled={saving}>
            {t('common.cancel', { ns: 'common', defaultValue: 'Mégse' })}
          </Button>
          <Button
            color="red"
            leftSection={<IconBan size={16} />}
            onClick={handleSave}
            loading={saving}
            disabled={reason.trim() === ''}
          >
            {t('requirements.cancel_btn', {
              ns: 'fe',
              defaultValue: 'Visszavonás',
            })}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
